<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { type ErpCounts, erpIntegrationApi } from '@/api/erp-integration';
import { apiErrorCode } from '@/utils/api-errors';
import { usePermissions } from '@/composables/usePermissions';
import ResponsiveTabs from '@/components/responsive-tabs.vue';
import ErpSalesOrdersList from './erp-sales-orders-list.vue';
import ErpEmployeesList from './erp-employees-list.vue';
import ErpUnmappedList from './erp-unmapped-list.vue';
import ErpSessionsList from './erp-sessions-list.vue';
import ErpTotals from './erp-totals.vue';
import ErpPatientsList from './erp-patients-list.vue';
import { actionError, connectionError } from './erp-messages';
import { formatDateTime } from './erp-format';

// "Integración Odoo" (ADR 0001, "Visibility"): what the company's ERP left in
// Kuido for a coordinator to decide, and the connection's health.

const { hasPermission } = usePermissions();
const queryClient = useQueryClient();
const canManage = computed(() => hasPermission.value('erp_integration', 'manage'));

const { data: overview } = useQuery({
  queryKey: ['erpIntegration', 'overview'],
  queryFn: erpIntegrationApi.overview,
  enabled: canManage,
});

const connection = computed(() => overview.value?.connection);
const counts = computed(() => overview.value?.counts);

const TABS: { value: string; title: string; icon: string; count?: keyof ErpCounts }[] = [
  { value: 'to_incorporate', title: 'Por incorporar', icon: 'mdi-account-plus',
    count: 'toIncorporate' },
  { value: 'without_line', title: 'Turnos sin presupuesto', icon: 'mdi-calendar-question',
    count: 'withoutLine' },
  { value: 'problems', title: 'Sesiones con problemas', icon: 'mdi-sync-alert',
    count: 'sessionProblems' },
  { value: 'adoption', title: 'Sesiones a mano', icon: 'mdi-hand-back-right', count: 'adoption' },
  { value: 'employees', title: 'Profesionales', icon: 'mdi-account-alert', count: 'employees' },
  { value: 'unmapped', title: 'Por mapear', icon: 'mdi-link-off', count: 'unmapped' },
  { value: 'patients', title: 'Pacientes incorporados', icon: 'mdi-account-check' },
  { value: 'totals', title: 'Cierre de mes', icon: 'mdi-scale-balance' },
];

// Each list's count in its title, once the overview arrived.
const tabs = computed(() => TABS.map(({ value, title, icon, count }) => {
  const rows = count && counts.value ? counts.value[count] : undefined;

  return { value, icon, title: rows === undefined ? title : `${title} (${rows})` };
}));

// Deep-linkable (?tab=without_line) without a client router.
function initialTab(): string {
  const requested = new URLSearchParams(window.location.search).get('tab');

  return TABS.some((tab) => tab.value === requested) ? requested as string : 'to_incorporate';
}

const activeTab = ref(initialTab());

// The tab stays in the URL, so a reload or a shared link opens it.
watch(activeTab, (tab) => {
  const url = new URL(window.location.href);
  url.searchParams.set('tab', tab);
  window.history.replaceState(null, '', url);
});

const snackbar = ref({ show: false, text: '', color: 'success' });

function notify(text: string, color = 'success') {
  snackbar.value = { show: true, text, color };
}

const { mutate: sync, isPending: isSyncing } = useMutation({
  mutationFn: erpIntegrationApi.sync,
  onSuccess: () => notify('¡Sincronización en curso! Las listas se actualizan en unos minutos.'),
  onError: (error) => notify(
    actionError(apiErrorCode(error), 'Ocurrió un error al sincronizar. Intenta nuevamente.'),
    'error',
  ),
});

// Anything the coordinator did changes the counts.
function refresh(message?: string) {
  queryClient.invalidateQueries({ queryKey: ['erpIntegration'] });
  if (message) notify(message);
}
</script>
<template>
  <v-container v-if="canManage">
    <h1 class="mb-4 text-h4">
      Integración Odoo
    </h1>
    <v-card
      flat
      class="mb-4 border"
    >
      <v-card-text>
        <div class="d-flex flex-wrap align-center ga-6">
          <div>
            <div class="text-caption text-uppercase text-medium-emphasis">
              Conexión
            </div>
            <v-chip
              v-if="connection"
              :color="connection.active ? 'success' : 'grey-darken-3'"
              size="small"
              :prepend-icon="connection.active ? 'mdi-check-circle' : 'mdi-pause-circle'"
            >
              {{ connection.active ? 'Activa' : 'Pausada' }}
            </v-chip>
          </div>
          <div>
            <div class="text-caption text-uppercase text-medium-emphasis">
              Última sincronización
            </div>
            <div class="text-body-1 font-weight-medium">
              {{ formatDateTime(connection?.lastPullAt) }}
            </div>
          </div>
          <div>
            <div class="text-caption text-uppercase text-medium-emphasis">
              Último envío de sesiones
            </div>
            <div class="text-body-1 font-weight-medium">
              {{ formatDateTime(connection?.lastPushAt) }}
            </div>
          </div>
          <div>
            <div class="text-caption text-uppercase text-medium-emphasis">
              Última reconciliación
            </div>
            <div class="text-body-1 font-weight-medium">
              {{ formatDateTime(connection?.reconciledAt) }}
            </div>
          </div>
          <div>
            <div class="text-caption text-uppercase text-medium-emphasis">
              Sesiones por enviar
            </div>
            <div class="text-body-1 font-weight-medium">
              {{ counts?.pendingSessions ?? '—' }}
            </div>
          </div>
          <v-spacer />
          <v-btn
            color="primary"
            prepend-icon="mdi-sync"
            :loading="isSyncing"
            :disabled="!connection?.active"
            @click="sync()"
          >
            Sincronizar ahora
          </v-btn>
        </div>
        <v-alert
          v-if="overview?.goLiveMissing.length"
          type="warning"
          variant="tonal"
          density="compact"
          role="status"
          class="mt-4"
        >
          {{ connection?.active ?
            'La integración está activa, pero ya no cumple todos sus requisitos. Avisa a ' +
            'Kuido. Requisitos pendientes:' :
            'Kuido aún está preparando la integración. Requisitos pendientes:' }}
          <ul class="ml-4">
            <li
              v-for="item in overview.goLiveMissing"
              :key="item"
            >
              {{ item }}
            </li>
          </ul>
        </v-alert>
        <v-alert
          v-if="connection?.lastErrorCode"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-4"
        >
          La última sincronización falló: {{ connectionError(connection.lastErrorCode) }}
          ({{ formatDateTime(connection.lastErrorAt) }}). Se reintenta sola; si persiste, avisa a
          Kuido.
        </v-alert>
      </v-card-text>
    </v-card>
    <v-card>
      <responsive-tabs
        v-model="activeTab"
        :items="tabs"
      />
      <v-window v-model="activeTab">
        <v-window-item value="to_incorporate">
          <erp-sales-orders-list
            v-if="activeTab === 'to_incorporate'"
            @changed="refresh"
          />
        </v-window-item>
        <v-window-item
          v-for="list in ['without_line', 'problems', 'adoption'] as const"
          :key="list"
          :value="list"
        >
          <erp-sessions-list
            v-if="activeTab === list"
            :list="list"
            @changed="refresh"
          />
        </v-window-item>
        <v-window-item value="employees">
          <erp-employees-list v-if="activeTab === 'employees'" />
        </v-window-item>
        <v-window-item value="unmapped">
          <erp-unmapped-list v-if="activeTab === 'unmapped'" />
        </v-window-item>
        <v-window-item value="patients">
          <erp-patients-list
            v-if="activeTab === 'patients'"
            @changed="refresh"
          />
        </v-window-item>
        <v-window-item value="totals">
          <erp-totals v-if="activeTab === 'totals'" />
        </v-window-item>
      </v-window>
    </v-card>
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      rounded="pill"
      :timeout="snackbar.color === 'error' ? 5000 : 4000"
    >
      <v-icon
        v-if="snackbar.color === 'success'"
        icon="mdi-check-circle"
        class="mr-2"
      />
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>
